import React, { useState, useEffect } from 'react';
import { productosAPI } from '../api/client';
import { useConfig } from '../context/ConfigContext';
import { Plus, Search, Edit, Trash2, Camera, Printer } from 'lucide-react';
import ProductModal from '../components/ProductModal';
import BarcodeScannerNew from '../components/BarcodeScannerNew';
import BarcodeGenerator from '../components/BarcodeGenerator';

const ProductosPage = () => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isBarcodeGeneratorOpen, setIsBarcodeGeneratorOpen] = useState(false);
  const [productoEditar, setProductoEditar] = useState(null);
  const { formatCurrency } = useConfig();

  useEffect(() => {
    loadProductos();
  }, []);

  const loadProductos = async () => {
    try {
      const response = await productosAPI.getAll();
      if (response.data.success) {
        setProductos(response.data.data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setBusqueda(e.target.value);
  };

  const handleBarcodeDetected = async (code) => {
    setIsScannerOpen(false);
    try {
      const response = await productosAPI.getByCodigo(code);
      if (response.data.success) {
        setProductoEditar(response.data.data);
        setIsModalOpen(true);
      }
    } catch (error) {
      alert('Producto no encontrado');
    }
  };

  const handleSave = async (formData) => {
    try {
      let response;
      if (productoEditar) {
        response = await productosAPI.update(productoEditar.id, formData);
      } else {
        response = await productosAPI.create(formData);
      }

      if (response.data.success) {
        alert(productoEditar ? 'Producto actualizado' : 'Producto creado');
        loadProductos();
        setIsModalOpen(false);
        setProductoEditar(null);
      } else {
        alert(response.data.message || 'Error al guardar producto');
      }
    } catch (error) {
      const mensaje = error.response?.data?.message || error.message || 'Error desconocido';
      if (mensaje.includes('SKU') || mensaje.includes('sku')) {
        alert('Error: El SKU ya existe. Por favor usa un SKU diferente.');
      } else if (mensaje.includes('codigo_barras') || mensaje.includes('código')) {
        alert('Error: El código de barras ya existe. Por favor usa uno diferente.');
      } else {
        alert('Error al guardar: ' + mensaje);
      }
    }
  };

  const handleEdit = (producto) => {
    setProductoEditar(producto);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) {
      return;
    }

    try {
      const response = await productosAPI.delete(id);
      if (response.data.success) {
        alert('Producto eliminado exitosamente');
        // Actualizar lista inmediatamente filtrando el producto eliminado
        setProductos(prevProductos => prevProductos.filter(p => p.id !== id));
        // También recargar desde el servidor para asegurar sincronización
        setTimeout(() => loadProductos(), 500);
      }
    } catch (error) {
      alert('Error al eliminar: ' + (error.response?.data?.message || error.message));
      // Recargar en caso de error para mantener sincronización
      loadProductos();
    }
  };

  const productosFiltrados = productos.filter(producto =>
    producto.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    producto.sku?.toLowerCase().includes(busqueda.toLowerCase()) ||
    producto.codigo_barras?.toLowerCase().includes(busqueda.toLowerCase())
  );

  const getStockBadge = (producto) => {
    const estado = producto?.stock_estado;
    const stock = Number.parseFloat(producto?.stock_total ?? 0);

    if (estado === 'sin_stock' || stock <= 0) {
      return { className: 'bg-red-100 text-red-800', label: 'Sin stock' };
    }
    if (estado === 'bajo') {
      return { className: 'bg-yellow-100 text-yellow-800', label: 'Bajo' };
    }
    return { className: 'bg-green-100 text-green-800', label: 'Disponible' };
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header con título y botones */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h1 className="text-2xl md:text-3xl font-bold">Productos</h1>
        
        {/* Contenedor de botones - responsivo */}
        <div className="flex gap-2">
          {/* Botón Imprimir - Solo icono en móvil */}
          <button 
            onClick={() => setIsBarcodeGeneratorOpen(true)}
            className="btn-secondary flex items-center gap-2 px-3 py-2 md:px-4"
            title="Imprimir Códigos"
          >
            <Printer className="w-5 h-5" />
            <span className="hidden sm:inline">Imprimir</span>
          </button>
          
          {/* Botón Escanear - Solo icono en móvil */}
          <button 
            onClick={() => setIsScannerOpen(true)}
            className="btn-secondary flex items-center gap-2 px-3 py-2 md:px-4"
            title="Escanear Código"
          >
            <Camera className="w-5 h-5" />
            <span className="hidden sm:inline">Escanear</span>
          </button>
          
          {/* Botón Nuevo Producto - Siempre con texto pero más corto en móvil */}
          <button 
            onClick={() => {
              setProductoEditar(null);
              setIsModalOpen(true);
            }}
            className="btn-primary flex items-center gap-1 sm:gap-2 px-3 py-2 md:px-4 whitespace-nowrap"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="sm:hidden">Nuevo</span>
            <span className="hidden sm:inline">Nuevo Producto</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={busqueda}
              onChange={handleSearch}
              placeholder="Buscar producto, SKU o código..."
              className="input-field pl-10 text-sm md:text-base"
            />
          </div>
        </div>

        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">SKU</th>
                <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Categoría</th>
                <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio</th>
                <th className="px-3 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Stock</th>
                <th className="px-3 md:px-6 py-2 md:py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {productosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-3 md:px-6 py-6 md:py-8 text-center text-gray-500 text-sm md:text-base">
                    {busqueda ? 'No se encontraron productos' : 'No hay productos registrados'}
                  </td>
                </tr>
              ) : (
                productosFiltrados.map((producto) => (
                <tr key={producto.id} className="hover:bg-gray-50 border-b">
                  <td className="px-3 md:px-6 py-3 md:py-4">
                    <div>
                      <div className="text-sm md:text-base font-medium text-gray-900">{producto.nombre}</div>
                      <div className="text-xs text-gray-500 sm:hidden">SKU: {producto.sku}</div>
                    </div>
                  </td>
                  <td className="px-3 md:px-6 py-3 md:py-4 hidden sm:table-cell text-sm">{producto.sku}</td>
                  <td className="px-3 md:px-6 py-3 md:py-4 hidden lg:table-cell text-sm">{producto.categoria_nombre || '-'}</td>
                  <td className="px-3 md:px-6 py-3 md:py-4">
                    <div className="text-sm md:text-base font-medium">{formatCurrency(producto.precio_venta)}</div>
                  </td>
                  <td className="px-3 md:px-6 py-3 md:py-4 hidden sm:table-cell">
                    {(() => {
                      const badge = getStockBadge(producto);
                      return (
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded ${badge.className}`}> 
                            {badge.label}
                          </span>
                          <span className="text-sm text-gray-700">{Number.parseFloat(producto.stock_total ?? 0)}</span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 md:px-6 py-3 md:py-4">
                    <div className="flex items-center justify-center gap-1 sm:gap-2">
                      <button
                        onClick={() => handleEdit(producto)}
                        className="p-1.5 md:p-2 text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4 md:w-5 md:h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(producto.id)}
                        className="p-1.5 md:p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4 md:w-5 md:h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de producto */}
      <ProductModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setProductoEditar(null);
        }}
        onSave={handleSave}
        producto={productoEditar}
      />

      {/* Scanner de código de barras */}
      <BarcodeScannerNew
        isOpen={isScannerOpen}
        onDetected={handleBarcodeDetected}
        onClose={() => setIsScannerOpen(false)}
      />

      {/* Generador de códigos de barras */}
      <BarcodeGenerator
        isOpen={isBarcodeGeneratorOpen}
        onClose={() => setIsBarcodeGeneratorOpen(false)}
      />
    </div>
  );
};

export default ProductosPage;
